import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { NS } from '../../../../i18n/constants/keys';
import { RootState } from '../../../../utils/redux_store/store';
import { CreateJobOpeningDialog } from '../../components';
import {
  JobOpeningsAppliedFilters,
  JobOpeningsControls,
  JobOpeningsFilterDrawer,
  JobOpeningsHeader,
  JobOpeningsTable,
  JobOpeningsTabs,
} from '../components';
import { JOB_OPENINGS_TAB_VALUES } from '../constants';
import { checkHasAnyActiveLicense } from '../Functions';
import { getJobOpeningsColumns } from '../Functions/getJobOpeningsColumns';
import { useJobOpenings } from '../Hooks';

export const JobOpeningsContainer = () => {
  const { t } = useTranslation(NS.adminPages);
  const table = useJobOpenings();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);

  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);
  const hasAnyLicense = checkHasAnyActiveLicense(subscriptions);

  const isClosedTab = table.activeTab === JOB_OPENINGS_TAB_VALUES.CLOSED;

  const columns = useMemo(
    () => getJobOpeningsColumns(t, table.onRowClick, isClosedTab),
    [t, table.onRowClick, isClosedTab],
  );
  const activeFilterCount = Object.keys(table.appliedFilters).length;

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Breadcrumbs & Header */}
      <JobOpeningsHeader totalRecords={table.totalRecords} isClosed={isClosedTab} />

      {/* Controls Bar: Tabs & Search/Action controls */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', xl: 'row' },
          alignItems: { xs: 'stretch', xl: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <JobOpeningsTabs
          activeTab={table.activeTab}
          onTabChange={table.setActiveTab}
          tabs={table.tabs}
        />

        <JobOpeningsControls
          searchJobTitle={table.searchJobTitle}
          onSearchChange={table.handleSearchChange}
          activeTab={table.activeTab}
          onOpenFilter={table.handleOpenFilter}
          activeFilterCount={activeFilterCount}
          onCreateClick={() => setIsCreateModalOpen(true)}
          hasAnyLicense={hasAnyLicense}
        />
      </Box>

      {/* Applied filter chips */}
      <JobOpeningsAppliedFilters
        appliedFilters={table.appliedFilters}
        onRemoveFilter={table.handleRemoveFilter}
        onClearFilters={table.handleClearFilters}
      />

      {/* Table & Pagination */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <JobOpeningsTable
          data={table.jobOpeningData}
          columns={columns}
          loading={table.loading}
          hasFetchedOnce={table.hasFetchedOnce}
          totalPages={table.totalPages}
          pageNumber={table.pageNumber}
          totalRecords={table.totalRecords}
          onPageChange={table.onPageChange}
          sort={table.sort}
          onSortChange={table.handleSortChange}
          onRowClick={table.onRowClick}
          isFiltersApplied={table.isFiltersApplied}
          onClearFilters={table.handleClearFilters}
        />
      </Box>

      {/* Filter Drawer */}
      <JobOpeningsFilterDrawer
        isOpen={table.filterOpen}
        onClose={table.handleCloseFilter}
        draftFilters={table.draftFilters}
        onDraftChange={table.handleDraftFilterChange}
        onManagerChange={table.handleManagerChange}
        onApply={table.handleApplyFilters}
        onClear={table.handleClearFilters}
        isFilterDisabled={table.isFilterDisabled}
        defaultEmployeeUserOptions={table.defaultEmployeeUserOptions}
        loadOptionsDebounced={table.loadOptionsDebounced}
      />

      {/* Create Dialog */}
      <CreateJobOpeningDialog
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccessClose={() => setIsCreateModalOpen(false)}
      />
    </Box>
  );
};
